import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Loader2, Radio } from "lucide-react";
import {
  formatApiError,
  getScanner,
  patchScanner,
  scanNow,
  toastApiError,
} from "@/lib/api";

const ASSET_CLASSES = [
  { key: "stocks", label: "Stocks" },
  { key: "etfs", label: "ETFs" },
  { key: "crypto", label: "Crypto" },
  { key: "fx", label: "FX (sim only)" },
];

/**
 * Scanner card — right-column widget.
 *
 * Shows: on/off toggle, per-cycle limits, last-cycle summary (screened count
 * + top candidates with scores), a SCAN NOW button (60s cooldown, budget-
 * counted), and the ETA of the next scheduled cycle.
 */
export default function ScannerCard({ portfolioId, portfolioType, onStatusChange }) {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const load = async () => {
    if (!portfolioId) return;
    try {
      const s = await getScanner(portfolioId);
      setState(s);
      onStatusChange?.(s);
    } catch (e) {
      // Silent on 404 (portfolio switch race)
    }
  };

  useEffect(() => {
    setState(null);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);

  if (!portfolioId) return null;

  const cfg = state?.config;
  const lc = state?.last_cycle;

  const patch = async (updates) => {
    setSaving(true);
    try {
      const r = await patchScanner(portfolioId, updates);
      setState((prev) => ({ ...prev, config: r.config }));
      load();
    } catch (e) {
      toastApiError(e);
    } finally {
      setSaving(false);
    }
  };

  const toggleClass = (key) => {
    if (!cfg) return;
    const cur = new Set(cfg.asset_classes || []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    if (cur.size === 0) {
      toast.error("Keep at least one asset class enabled.");
      return;
    }
    patch({ asset_classes: [...cur] });
  };

  const doScanNow = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      await scanNow(portfolioId);
      toast.success("Scan cycle started.");
      // Optimistically flip enabled ON if the user just triggered a scan.
      setTimeout(load, 800);
    } catch (e) {
      toastApiError(e);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div data-testid="scanner-card" className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <span className="aurion-section-title">Scanner</span>
          <span
            data-testid="scanner-enabled-indicator"
            className={`h-1.5 w-1.5 rounded-full ${
              cfg?.enabled
                ? "bg-[var(--aurion-gold)] shadow-[0_0_6px_var(--aurion-gold-glow)]"
                : "bg-[var(--aurion-muted)]"
            }`}
          />
        </div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--aurion-muted)]">
          {cfg?.enabled ? "auto" : "paused"}
        </div>
      </div>

      {/* Asset-class chips */}
      <div className="flex flex-wrap gap-1">
        {ASSET_CLASSES.map((c) => {
          const active = (cfg?.asset_classes || []).includes(c.key);
          const disabled =
            !cfg || saving || (c.key === "fx" && portfolioType === "alpaca");
          return (
            <button
              key={c.key}
              data-testid={`scanner-class-${c.key}`}
              disabled={disabled}
              onClick={() => toggleClass(c.key)}
              title={
                c.key === "fx" && portfolioType === "alpaca"
                  ? "FX not tradable on Alpaca"
                  : undefined
              }
              className={`border px-1.5 py-[2px] text-[10px] uppercase tracking-widest transition-colors ${
                active
                  ? "border-[var(--aurion-gold)]/60 bg-[var(--aurion-gold)]/10 text-[var(--aurion-gold)]"
                  : "border-[var(--aurion-border)] text-[var(--aurion-muted)] disabled:opacity-40"
              }`}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Compact metrics row */}
      <div className="grid grid-cols-2 gap-1 text-[10px]">
        <div className="border border-[var(--aurion-border)] px-2 py-1">
          <div className="uppercase tracking-widest text-[var(--aurion-muted)]">
            Next
          </div>
          <div
            data-testid="scanner-next"
            className="font-mono text-[11px] text-[var(--aurion-text)]"
          >
            {!cfg?.enabled
              ? "—"
              : state?.next_scan_in_seconds == null
              ? "soon"
              : formatSeconds(state.next_scan_in_seconds)}
          </div>
        </div>
        <div className="border border-[var(--aurion-border)] px-2 py-1">
          <div className="uppercase tracking-widest text-[var(--aurion-muted)]">
            Budget
          </div>
          <div
            data-testid="scanner-budget"
            className="font-mono text-[11px] text-[var(--aurion-text)]"
          >
            {state?.daily_budget_used ?? 0}/{state?.daily_budget_total ?? "—"}
          </div>
        </div>
      </div>

      {/* Last cycle */}
      <div
        data-testid="scanner-last-cycle"
        className="border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-2 py-2"
      >
        <div className="flex items-baseline justify-between text-[10px] uppercase tracking-widest">
          <span className="text-[var(--aurion-muted)]">Last cycle</span>
          {lc?.trigger ? (
            <span className="text-[var(--aurion-muted-strong)]">
              {lc.trigger === "manual_scan" ? "manual" : "auto"}
            </span>
          ) : null}
        </div>
        {!lc ? (
          <div className="mt-1 text-[10px] text-[var(--aurion-muted)]">
            — no cycles yet —
          </div>
        ) : (
          <div className="mt-1 space-y-1">
            <div className="font-mono text-[10px] text-[var(--aurion-text-dim)]">
              {lc.screened} screened · {lc.dispatched?.length || 0} dispatched
              {lc.skipped_reason ? ` · ${lc.skipped_reason}` : ""}
            </div>
            {(lc.top_candidates || []).slice(0, 5).map((c) => {
              const wasDispatched = (lc.dispatched || []).includes(c.symbol);
              return (
                <div
                  key={c.symbol}
                  className="flex items-baseline justify-between font-mono text-[10px]"
                >
                  <span
                    className={
                      wasDispatched
                        ? "text-[var(--aurion-gold)]"
                        : "text-[var(--aurion-text-dim)]"
                    }
                  >
                    {wasDispatched ? "▸ " : "  "}
                    {c.symbol}
                  </span>
                  <span className="tabular-nums text-[var(--aurion-muted-strong)]">
                    {(c.score ?? 0).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="flex items-center gap-2">
        <button
          data-testid="scanner-run-now"
          onClick={doScanNow}
          disabled={!cfg || scanning}
          className="flex flex-1 items-center justify-center gap-1.5 border border-[var(--aurion-gold)]/40 bg-[var(--aurion-gold)]/10 px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[var(--aurion-gold)] transition-colors hover:bg-[var(--aurion-gold)]/20 disabled:opacity-40"
        >
          {scanning ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Zap className="h-3 w-3" />
          )}
          Scan now
        </button>
        <button
          data-testid="scanner-advanced"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
        >
          <Radio className="h-3 w-3" />
        </button>
      </div>

      {advancedOpen && cfg ? (
        <div className="mt-1 grid grid-cols-2 gap-1 text-[10px]">
          <label className="flex items-center justify-between border border-[var(--aurion-border)] px-2 py-1">
            <span className="uppercase tracking-widest text-[var(--aurion-muted)]">
              Every
            </span>
            <select
              data-testid="scanner-interval"
              value={cfg.scan_interval_minutes}
              onChange={(e) => patch({ scan_interval_minutes: Number(e.target.value) })}
              className="bg-transparent font-mono text-[11px] text-[var(--aurion-text)] focus:outline-none"
            >
              {[15, 30, 60, 120, 240, 480].map((m) => (
                <option key={m} value={m} className="bg-[var(--aurion-panel)]">
                  {m}m
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center justify-between border border-[var(--aurion-border)] px-2 py-1">
            <span className="uppercase tracking-widest text-[var(--aurion-muted)]">
              Per cycle
            </span>
            <select
              data-testid="scanner-per-cycle"
              value={cfg.max_analyses_per_cycle}
              onChange={(e) =>
                patch({ max_analyses_per_cycle: Number(e.target.value) })
              }
              className="bg-transparent font-mono text-[11px] text-[var(--aurion-text)] focus:outline-none"
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n} className="bg-[var(--aurion-panel)]">
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label className="col-span-2 flex items-center justify-between border border-[var(--aurion-border)] px-2 py-1">
            <span className="uppercase tracking-widest text-[var(--aurion-muted)]">
              Daily budget
            </span>
            <select
              data-testid="scanner-budget-total"
              value={cfg.daily_analysis_budget}
              onChange={(e) =>
                patch({ daily_analysis_budget: Number(e.target.value) })
              }
              className="bg-transparent font-mono text-[11px] text-[var(--aurion-text)] focus:outline-none"
            >
              {[5, 10, 20, 40, 100].map((n) => (
                <option key={n} value={n} className="bg-[var(--aurion-panel)]">
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
    </div>
  );
}

function formatSeconds(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
}
